import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import AnimateIn from './AnimateIn';

const faqs = [
  {
    q: 'What is ActCheck?',
    a: 'ActCheck is a mobile-first compliance, tenancy, documentation, rights, and reminder app designed for landlords and tenants in England\'s private rented sector. It helps you stay organised, track documents, manage deadlines, and understand your obligations and rights.',
  },
  {
    q: 'Is ActCheck a mobile app or a website?',
    a: 'ActCheck is primarily a mobile app, available for both iOS and Android. This website provides information about the product. To use ActCheck, you need to download the mobile app.',
  },
  {
    q: 'Can I use ActCheck on desktop?',
    a: 'ActCheck is currently designed as a mobile-first experience. The app is available on iOS and Android devices. Desktop access may be considered in the future.',
  },
  {
    q: 'Is ActCheck legal advice?',
    a: 'No. ActCheck provides workflow and information support to help you stay organised and understand what applies to your situation. It is not legal advice, and ActCheck is not a law firm or legal representative. Legal information should always be checked with the latest source and professional advice where needed.',
  },
  {
    q: 'Is ActCheck for landlords or tenants?',
    a: 'ActCheck is designed for both landlords and tenants. Landlords can use it for compliance tracking, document management, and deadline reminders. Tenants can use it to understand their rights, track repairs, and stay organised.',
  },
  {
    q: 'Does ActCheck support reminders and document tracking?',
    a: 'Yes. ActCheck includes automated reminders for key dates, document expiry alerts, and a secure document vault so you can keep everything in one place.',
  },
  {
    q: 'Does ActCheck support tenant-rights pack workflows?',
    a: 'Yes. ActCheck includes workflows to help landlords deliver required documents and information to tenants, and helps tenants understand what they should have received.',
  },
  {
    q: 'Do I need to download the app to use ActCheck?',
    a: 'Yes, you need to download the ActCheck mobile app to use the product. The app is available on the App Store for iOS and Google Play for Android. This website is for product information only.',
  },
];

function FAQItem({ faq, isOpen, onToggle, index }) {
  return (
    <div
      data-testid={`faq-item-${index}`}
      className={`border rounded-xl transition-all duration-300 ${
        isOpen ? 'border-compliance-green/30 bg-compliance-green/[0.03] shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      <button
        onClick={onToggle}
        data-testid={`faq-toggle-${index}`}
        className="w-full flex items-center justify-between px-6 py-5 text-left"
        aria-expanded={isOpen}
      >
        <span className="font-heading font-semibold text-[15px] text-ink pr-4">{faq.q}</span>
        <ChevronDown
          size={20}
          className={`text-slate-text flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-compliance-green' : ''}`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <p className="px-6 pb-5 font-body text-sm text-slate-text leading-relaxed">{faq.a}</p>
      </div>
    </div>
  );
}

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section id="faq" data-testid="faq-section" className="py-20 sm:py-28 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimateIn>
          <div className="text-center mb-14">
            <span className="inline-block text-compliance-green font-body font-semibold text-sm tracking-wide uppercase mb-3">
              FAQ
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-ink mb-4">
              Frequently asked questions
            </h2>
          </div>
        </AnimateIn>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <AnimateIn key={faq.q} delay={i * 60}>
              <FAQItem
                faq={faq}
                index={i}
                isOpen={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? null : i)}
              />
            </AnimateIn>
          ))}
        </div>
      </div>
    </section>
  );
}
